
import { useState, useCallback } from "react";
import { FolderKanban, Trash2 } from "lucide-react";
import type { Project } from "@/types/crm";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { AskAiButton } from "@/components/assistant/assistant-provider";

interface ProjectDetailHeaderProps {
  project: Project;
  onSaveName: (name: string) => void;
  onDelete?: () => void;
}

export function ProjectDetailHeader({ project, onSaveName, onDelete }: ProjectDetailHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = useCallback(() => {
    setDraft(project.name);
    setEditing(true);
  }, [project.name]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== project.name) {
      onSaveName(trimmed);
    }
  }, [draft, project.name, onSaveName]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") { e.preventDefault(); commit(); }
      else if (e.key === "Escape") { e.preventDefault(); cancel(); }
    },
    [commit, cancel]
  );

  return (
    <div className="space-y-1.5">
      <Breadcrumb
        items={[
          { label: "Projects", href: "/dashboard/projects" },
          { label: project.name },
        ]}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <FolderKanban className="h-5 w-5 text-muted-foreground shrink-0" />
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
            className="text-2xl font-semibold tracking-tight bg-transparent border-b-2 border-primary outline-none w-full max-w-md"
          />
        ) : (
          <h1
            className="text-2xl font-semibold tracking-tight truncate cursor-text hover:opacity-80 transition-opacity"
            onClick={startEdit}
            title="Click to edit"
          >
            {project.name}
          </h1>
        )}
        <div className="ml-auto flex items-center gap-1">
          <AskAiButton />
          {onDelete && (
            <Button variant="ghost" size="icon" className="size-9 text-muted-foreground hover:text-destructive" onClick={onDelete} title="Delete project">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {project.description && (
        <p className="text-sm text-muted-foreground">{project.description}</p>
      )}
    </div>
  );
}
